import { useQueryClient } from '@tanstack/react-query';
import type { CustomerDTO, CustomerRowDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Input } from '@bifeai/ui';
import { Search, UserPlus, X } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useCustomers } from '../../lib/customers';
import { formatMoney } from '../../lib/format';

/** Lo que se puede fiar sin autorización. */
export const available = (c: Pick<CustomerRowDTO, 'creditLimitCents' | 'balanceCents'>) => Math.max(0, c.creditLimitCents - c.balanceCents);

/**
 * Elegir el cliente en el mostrador: se busca por nombre, teléfono o DNI.
 * Si no está, se da de alta ahí mismo con nombre y teléfono (sin fiado: el límite lo pone el dueño).
 */
export function CustomerPicker({
  value,
  onChange,
  autoFocus,
}: {
  value: CustomerRowDTO | null;
  onChange: (c: CustomerRowDTO | null) => void;
  autoFocus?: boolean;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const list = useCustomers(search);

  if (value) {
    return (
      <div className="bf-cust-card">
        <div>
          <strong>{value.name}</strong>
          <small>
            Debe {formatMoney(value.balanceCents)} ·{' '}
            {value.creditLimitCents > 0 ? `puede fiar ${formatMoney(available(value))} más` : 'sin fiado habilitado'}
          </small>
        </div>
        {value.overdue && <Badge tone="warning">Deuda atrasada</Badge>}
        <button type="button" className="bf-icon-btn" aria-label="Quitar cliente" onClick={() => onChange(null)}>
          <X />
        </button>
      </div>
    );
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const c = await api.post<CustomerDTO>('/customers', {
        name: search.trim(),
        phone: phone.trim() || null,
      });
      await qc.invalidateQueries({ queryKey: ['customers'] });
      setCreating(false);
      setSearch('');
      onChange(c);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const rows = search.trim() ? (list.data ?? []).slice(0, 6) : [];
  return (
    <div className="bf-cust-pick">
      <div className="bf-search">
        <Search aria-hidden />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCreating(false);
          }}
          placeholder="Nombre del cliente (Enter crea uno nuevo)"
          aria-label="Buscar cliente"
          onKeyDown={(e) => {
            // Enter: si hay uno con ese nombre exacto lo elige; si no, lo crea con ese nombre (sin pedir más datos).
            if (e.key !== 'Enter' || search.trim().length < 2) return;
            e.preventDefault();
            const exact = (list.data ?? []).find((c) => c.name.toLowerCase() === search.trim().toLowerCase());
            if (exact) onChange(exact);
            else void create();
          }}
          autoFocus={autoFocus}
        />
      </div>
      {rows.length > 0 && (
        <div className="bf-cust-results" role="listbox" aria-label="Clientes">
          {rows.map((c) => (
            <button key={c.id} type="button" role="option" aria-selected="false" className="bf-cust-result" onClick={() => onChange(c)}>
              <span>
                <strong>{c.name}</strong>
                <small>{[c.phone, c.docNumber].filter(Boolean).join(' · ') || 'Sin datos de contacto'}</small>
              </span>
              <span className={`bf-num ${c.balanceCents > 0 ? 'bf-text-danger' : 'bf-text-muted'}`}>
                {c.balanceCents > 0 ? `Debe ${formatMoney(c.balanceCents)}` : 'Al día'}
              </span>
            </button>
          ))}
        </div>
      )}
      {search.trim().length >= 2 && !creating && (
        <Button variant="ghost" size="sm" icon={<UserPlus />} onClick={() => setCreating(true)}>
          Nuevo cliente: “{search.trim()}”
        </Button>
      )}
      {creating && (
        <div className="bf-cust-new">
          {error && <Callout tone="danger">{error}</Callout>}
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Teléfono / WhatsApp (opcional)"
            inputMode="tel"
            aria-label="Teléfono"
            autoFocus
          />
          <Button variant="primary" size="sm" loading={busy} onClick={() => void create()}>
            Crear y elegir
          </Button>
        </div>
      )}
    </div>
  );
}
